import type Highcharts from "highcharts";
import { color } from "@ui";
import t from "../../../../i18n";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import type { NoFailureMetadata } from "../../Chart/PointMetadata";
import { NO_FAILURE_METADATA } from "../../Chart/PointMetadata";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { analysisStep } from "diagnostics-domain/analysisStep";
import type { Project } from "../../../../api/useProjects";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";

export const NO_FAILURES_SERIE_ID = "no-failures";

const marker = {
  radius: 6,
  symbol: "circle",
};

export default function getNoFailureSeries({
  yAxisId,
  analyses,
  project,
  indexedFailureValues,
  indexedFailureMetadata,
  legendIndex,
}: {
  yAxisId: string;
  analyses: ActiveAnalysis[];
  project: Project;
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  legendIndex: number;
}): Highcharts.SeriesLineOptions {
  const data = analyses.reduce<Highcharts.PointOptionsObject[]>((acc, analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    const step = analysisStep(project, analysis);
    const isAnalysisOutdated = step !== null;

    if (isAnalysisOutdated) return acc;

    const custom: NoFailureMetadata = {
      type: NO_FAILURE_METADATA,
      group: analysis.group,
      label: analysis.label,
      analysisId: analysis.id,
    };

    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      const custom: NoFailureMetadata = {
        type: NO_FAILURE_METADATA,
        group: null,
        label: null,
        analysisId: `intercycle-${analysis.id}-${previousAnalysis.id}`,
      };

      acc.push({
        id: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        x: (analysis.date + previousAnalysis.date) / 2,
        y: null,
        allowPointSelect: false,
        custom,
      });
    }

    const metadatas = indexedFailureMetadata.getDateMetadata(analysis.date);
    let hasRejected = false;
    if (metadatas)
      for (const [_, metadata] of metadatas) {
        if (metadata.status === "REJECTED") {
          hasRejected = true;
          break;
        }
      }

    if (
      analysis.status !== "DIAGNOSIS" ||
      indexedFailureValues[analysis.date] !== undefined ||
      hasRejected
    ) {
      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        custom,
        y: null,
      });
      return acc;
    }

    acc.push({
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      custom,
      description: t("diagnosis.chart.tooltip.description.no_failure_suspected"),
      y: 0,
      marker,
    });

    return acc;
  }, []);

  return {
    name: t("diagnosis.legend.no_failure"),
    yAxis: yAxisId,
    id: NO_FAILURES_SERIE_ID,
    color: color.lightNitrogen900,
    type: "line",
    allowPointSelect: false,
    zIndex: zIndexChart.lineSeries + 1,
    cursor: "default",
    data,
    replicateInNavigator: false,
    softThreshold: false,
    showInLegend: true,
    legendIndex,
  };
}
